import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useT } from '../../i18n';

// Pay-first add-to-cart tracker: which stories get added to the cart, split by
// how the visitor entered pay-first — 'pay_first_url' (?preview=0 ad links),
// 'pay_first' (A/B flag), or anything else — plus the downstream funnel and a
// feed of the latest adds. Data: GET /api/admin/analytics/add-to-carts.
const RANGES = [1, 7, 14, 30];
const FUNNEL_STEPS = ['payfirst_add_to_cart', 'payfirst_checkout_view', 'payfirst_checkout_submit', 'payfirst_create_story'];

function variantLabel(t, v) {
  if (v === 'pay_first_url') return t.adminAtcVariantUrl;
  if (v === 'pay_first') return t.adminAtcVariantFlag;
  if (v === 'control') return t.adminAtcVariantControl;
  return t.adminAtcVariantUnknown;
}

// Count for one (variant) inside a story row; control+unknown fold together
// under "other" so the table stays three columns.
function variantCount(variants, which) {
  if (which === 'other') {
    return variants
      .filter((x) => x.variant !== 'pay_first_url' && x.variant !== 'pay_first')
      .reduce((sum, x) => sum + x.count, 0);
  }
  return variants.find((x) => x.variant === which)?.count || 0;
}

function fmtTime(d) {
  const dt = new Date(d);
  return `${dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export default function AdminAddToCarts() {
  const t = useT();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    api.get(`/api/admin/analytics/add-to-carts?days=${days}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [days]);

  const stepLabels = {
    payfirst_add_to_cart: t.adminAtcStepAtc,
    payfirst_checkout_view: t.adminAtcStepCheckoutView,
    payfirst_checkout_submit: t.adminAtcStepCheckoutSubmit,
    payfirst_create_story: t.adminAtcStepCreated,
  };
  const funnelCell = (type, variant) => {
    const row = data?.funnel?.find((f) => f.type === type && f.variant === variant);
    return row ? `${row.count} (${row.visitors})` : '—';
  };

  return (
    <div className="admin-page">
      <h1 className="page-title">{t.adminAtcTitle}</h1>
      <p className="admin-atc-hint">{t.adminAtcHint}</p>

      <div className="admin-atc-ranges">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={days === r ? 'admin-btn-primary' : 'admin-btn-secondary'}
            onClick={() => setDays(r)}
          >
            {t[`adminAtcRange${r}`]}
          </button>
        ))}
      </div>

      {error && <p className="admin-modal-error">{error}</p>}
      {!data && !error && <div className="loader" />}

      {data && (
        <>
          {/* Funnel: count (unique visitors) per step × cohort. */}
          <h2 className="admin-atc-section">{t.adminAtcFunnelTitle}</h2>
          <div className="admin-atc-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t.adminAtcStep}</th>
                  <th>{t.adminAtcVariantUrl}</th>
                  <th>{t.adminAtcVariantFlag}</th>
                  <th>{t.adminAtcVariantControl}</th>
                </tr>
              </thead>
              <tbody>
                {FUNNEL_STEPS.map((s) => (
                  <tr key={s}>
                    <td>{stepLabels[s]}</td>
                    <td>{funnelCell(s, 'pay_first_url')}</td>
                    <td>{funnelCell(s, 'pay_first')}</td>
                    <td>{funnelCell(s, 'control')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-story adds. */}
          <h2 className="admin-atc-section">{t.adminAtcByStoryTitle}</h2>
          {data.byStory.length === 0 ? (
            <p className="empty-state">{t.adminAtcNoData}</p>
          ) : (
            <div className="admin-atc-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t.adminAtcStory}</th>
                    <th>{t.adminAtcVariantUrl}</th>
                    <th>{t.adminAtcVariantFlag}</th>
                    <th>{t.adminAtcVariantControl}</th>
                    <th>{t.adminAtcTotal}</th>
                    <th>{t.adminAtcLast}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byStory.map((s) => (
                    <tr key={s.slug || s.name}>
                      <td>{s.name || s.slug}</td>
                      <td>{variantCount(s.variants, 'pay_first_url') || '—'}</td>
                      <td>{variantCount(s.variants, 'pay_first') || '—'}</td>
                      <td>{variantCount(s.variants, 'other') || '—'}</td>
                      <td><strong>{s.total}</strong></td>
                      <td>{fmtTime(s.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Latest adds, newest first — the "follow it live" feed. */}
          <h2 className="admin-atc-section">{t.adminAtcRecentTitle}</h2>
          {data.recent.length === 0 ? (
            <p className="empty-state">{t.adminAtcNoData}</p>
          ) : (
            <div className="admin-atc-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t.adminAtcTime}</th>
                    <th>{t.adminAtcStory}</th>
                    <th>{t.adminAtcSource}</th>
                    <th>{t.adminAtcVisitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((e) => (
                    <tr key={e._id}>
                      <td>{fmtTime(e.createdAt)}</td>
                      <td>{e.name || e.slug}</td>
                      <td>
                        <span className={`atc-variant-chip atc-variant-${e.variant || 'unknown'}`}>
                          {variantLabel(t, e.variant)}
                        </span>
                      </td>
                      <td className="admin-atc-visitor">{(e.visitorId || '').slice(0, 8)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
